import React from 'react'

import { lightenColor } from '@rewardops/utils'

import { SortOptionType, SortOrder } from '~/app/common/enums'
import { WidgetContainer } from '../contentful/widget-container'
import { useApi } from '../hooks/use-api'
import { useItems } from '../hooks/use-items'
import { DateSelector } from './date-selector'
import {
  ComparisonCharts,
  ComparisonTable,
  EarningHistory,
  Engagement,
  Goals,
  Heirarchy,
  Leaderboard,
  NeedsAttention,
  Overview,
  PendingManualAdjustments,
  Performance,
  PoinstBalanceSection,
  Promotions,
  Rankings,
  RewardsCatalog,
  Sales,
  TierDistribution,
  TierProgress,
} from './sections'
import { PAWSectionType } from './sections/paw-section-types'

// This needs to be outside the component definition so that it does not get defined on every render, makes lazy loading better optimized that way
const sectionRegistry: Record<string, React.ComponentType<PAWSectionType>> = {
  tier_progress: TierProgress,
  points_balance: PoinstBalanceSection,
  performance: Performance,
  promotions: Promotions,
  sales: Sales,
  earning_history: EarningHistory,
  rewards_catalog: RewardsCatalog,
  rankings: Rankings,
  overview: Overview,
  goals: Goals,
  comparison_charts: ComparisonCharts,
  leaderboard: Leaderboard,
  engagement: Engagement,
  pending_manual_adjustments: PendingManualAdjustments,
  heirarchy: Heirarchy,
  comparison_table: ComparisonTable,
  tier_distribution: TierDistribution,
  needs_attention: NeedsAttention,
}

type GenericPAWDashboardProps = {
  content: any
  role: string
}

// This component will act as the dashboard for all roles in PAW to centralize the logic
const GenericPAWDashboard: React.FC<GenericPAWDashboardProps> = ({
  content,
  role,
}: GenericPAWDashboardProps) => {
  const { tenant } = useApi()

  const { loading, items } = useItems(
    {
      isFeatured: true,
      sort: { by: SortOptionType.CreatedDate, order: SortOrder.Descending },
    },
    1,
    12,
  )

  // TODO: update this combined data when API starts working
  const combinedData = {
    items,
    isLoading: loading,
    content,
    role,
  }

  const backgroundColor = lightenColor(
    tenant?.customStyling?.primaryColor ?? '#FFFFFF',
    60,
  )

  return (
    <div id="generic-paw-dashboard" style={{ backgroundColor }}>
      <main className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-8">
          {content?.sections?.map((section: { sectionId: string }) => {
            const SectionComponent = sectionRegistry?.[section?.sectionId]

            if (!SectionComponent) return null

            return (
              <React.Fragment key={section?.sectionId}>
                {section?.sectionId === 'performance' ? (
                  <div className="flex justify-end">
                    <DateSelector />
                  </div>
                ) : null}

                {section?.sectionId === 'earning_history' ? (
                  <WidgetContainer
                    contentId={
                      tenant?.pawDashboardConfig?.activityWidgetId ??
                      'paw-dashboard-activity-widget'
                    }
                  />
                ) : (
                  <SectionComponent content={section} data={combinedData} />
                )}
              </React.Fragment>
            )
          })}
        </div>
      </main>
    </div>
  )
}

export { GenericPAWDashboard }
